import React, { useState, useMemo, useEffect } from "react";
import { categoryTree, traiteurCategoryTree, getCategoryChildrenForUnit } from "../../data/categoryTree";
import ProductInfoModal from "./ProductInfoModal";
import { getProductImageFallback } from "../../data/storefront";

const formatPrice = (v) => `${Number(v || 0).toFixed(2)} €`;

const CatalogueBrowser = ({ catalog, addToCart, setSelectedProductForNumpad, isWeightedProduct, businessUnit = "alimentation", menus = [] }) => {
  // Navigation path: array of category names. Empty means root.
  const [path, setPath] = useState([]);
  const [infoProduct, setInfoProduct] = useState(null);
  
  // For pagination / infinite scroll simulation
  const [visibleCount, setVisibleCount] = useState(30);

  const activeTree = businessUnit === "traiteur" ? traiteurCategoryTree : categoryTree;

  useEffect(() => {
    setPath([]);
    setVisibleCount(30);
  }, [businessUnit]);

  const currentLevel = path.length === 0 ? "root" : path.length === 1 ? "top" : "leaf";
  const currentCategoryName = path.length > 0 ? path[path.length - 1] : null;

  // Helper to get a representative image for a category by finding the first product in it
  const getCategoryImage = (categoryName) => {
    const validCategories = getCategoryChildrenForUnit(businessUnit, categoryName);
    const firstProduct = catalog.find((p) => validCategories.includes(p.category));
    return firstProduct ? (firstProduct.imageUrl || getProductImageFallback(firstProduct)) : null;
  };

  const currentNodes = useMemo(() => {
    if (currentLevel === "root") {
      return activeTree.map(c => ({
        type: 'category',
        name: c.name,
        image: getCategoryImage(c.name)
      }));
    }
    
    if (currentLevel === "top") {
      const topNode = activeTree.find(c => c.name === path[0]);
      if (!topNode) return [];
      
      return topNode.children.map(c => ({
        type: 'category',
        name: c.name,
        image: getCategoryImage(c.name)
      }));
    }
    
    // Leaf level: show products
    return [];
  }, [currentLevel, path, catalog, activeTree, businessUnit]);

  const currentProducts = useMemo(() => {
    if (currentLevel !== "leaf") return [];
    const validCategories = getCategoryChildrenForUnit(businessUnit, currentCategoryName);
    return catalog.filter((p) => validCategories.includes(p.category));
  }, [currentLevel, currentCategoryName, catalog, businessUnit]);

  const displayedProducts = currentProducts.slice(0, visibleCount);
  const hasMoreProducts = visibleCount < currentProducts.length;

  const navigateTo = (categoryName) => {
    setPath([...path, categoryName]);
    setVisibleCount(30); // reset visible count
  };

  const navigateUp = (index) => {
    setPath(path.slice(0, index + 1));
    setVisibleCount(30);
  };
  
  const handleProductClick = (product) => {
    if (isWeightedProduct(product) || product.price_per_kg) {
      setSelectedProductForNumpad(product);
    } else {
      addToCart(product, 1);
    }
  };

  const handleInfoClick = (e, product) => {
    e.stopPropagation();
    setInfoProduct(product);
  };

  return (
    <div className="d-flex flex-column h-100" style={{ backgroundColor: "var(--pos-bg-elevated)" }}>
      {/* Breadcrumb */}
      <div className="pos-catalogue-breadcrumb p-3 d-flex align-items-center flex-wrap gap-2 border-bottom" style={{ borderColor: "var(--pos-line)", backgroundColor: "var(--pos-bg-deep)" }}>
        <button 
          className="btn btn-sm text-white fw-bold d-flex align-items-center border-0" 
          onClick={() => { setPath([]); setVisibleCount(30); }}
          style={{ padding: 0, color: businessUnit === "traiteur" ? "#f97316" : "inherit" }}
        >
          {businessUnit === "traiteur" ? "TRAITEUR" : "CATALOGUE"}
        </button>
        
        {path.map((segment, idx) => (
          <React.Fragment key={idx}>
            <span className="text-muted mx-1">/</span>
            <button 
              className="btn btn-sm text-white fw-bold d-flex align-items-center border-0" 
              onClick={() => navigateUp(idx)}
              style={{ padding: 0 }}
            >
              {segment}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="flex-grow-1 overflow-auto p-3">
        {/* Menues / Accès rapide (Traiteur root only) */}
        {businessUnit === "traiteur" && currentLevel === "root" && menus.length > 0 && (
          <>
            <div className="d-flex align-items-center gap-2 mb-2">
              <i className="bi bi-stars text-warning"></i>
              <span className="fw-bold text-white text-uppercase" style={{ fontSize: "0.8rem", letterSpacing: "0.08em" }}>Menues / Accès rapide</span>
            </div>
            <div className="pos-catalogue-grid mb-4">
              {menus.map((menu) => (
                <div
                  key={menu.id}
                  className="pos-menu-tile shadow-sm cursor-pointer d-flex flex-column align-items-center justify-content-center text-center p-2"
                  onClick={() => addToCart(menu, 1)}
                >
                  <span className="fw-bold text-white text-uppercase" style={{ fontSize: "0.85rem", textShadow: "1px 1px 3px rgba(0,0,0,0.8)" }}>
                    {menu.name}
                  </span>
                  <span className="fw-bold text-white mt-1" style={{ fontSize: "1.05rem", textShadow: "1px 1px 3px rgba(0,0,0,0.8)" }}>
                    {formatPrice(menu.price)}
                  </span>
                  {Array.isArray(menu.components) && menu.components.length > 0 && (
                    <span className="text-white-50 mt-1" style={{ fontSize: "0.62rem", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {menu.components.join(" · ")}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Category Tiles */}
        {currentNodes.length > 0 && (
          <div className="pos-catalogue-grid mb-4">
            {currentNodes.map((node) => (
              <div 
                key={node.name} 
                className="pos-category-tile shadow-sm position-relative overflow-hidden cursor-pointer"
                onClick={() => navigateTo(node.name)}
              >
                {node.image && (
                  <img 
                    src={node.image} 
                    alt={node.name} 
                    className="position-absolute w-100 h-100 object-fit-cover" 
                    style={{ opacity: 0.3, zIndex: 1, top: 0, left: 0 }} 
                  />
                )}
                <div className="position-relative w-100 h-100 d-flex align-items-center justify-content-center text-center p-2" style={{ zIndex: 2 }}>
                  <span className="fw-bold text-white text-uppercase" style={{ fontSize: "0.9rem", textShadow: "1px 1px 3px rgba(0,0,0,0.8)" }}>
                    {node.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Product Cards Grid */}
        {currentLevel === "leaf" && displayedProducts.length > 0 && (
          <>
            <div className="pos-catalogue-grid">
              {displayedProducts.map((product) => (
                <div 
                  key={product.id} 
                  className="pos-product-card shadow-sm cursor-pointer"
                  onClick={() => handleProductClick(product)}
                >
                  <div className="pos-product-image-container position-relative bg-white">
                    <img 
                      src={product.imageUrl || getProductImageFallback(product)} 
                      alt={product.name} 
                      className="pos-product-image p-2"
                    />
                    <button 
                      className="pos-product-info-btn position-absolute border-0 rounded-circle shadow"
                      onClick={(e) => handleInfoClick(e, product)}
                      title="Plus d'informations"
                    >
                      <i className="bi bi-plus text-danger fs-4 d-flex align-items-center justify-content-center h-100"></i>
                    </button>
                  </div>
                  <div className="p-2 d-flex flex-column" style={{ flexGrow: 1, backgroundColor: "#e2e8f0" }}>
                    <div className="text-dark fw-bold mb-1 lh-sm" style={{ fontSize: "0.8rem", flexGrow: 1, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                      {product.name}
                    </div>
                    <div className={product.stock !== undefined && product.stock !== null && Number(product.stock) <= 0 ? "text-danger fw-bold mb-1" : "text-success fw-bold mb-1"} style={{ fontSize: "0.7rem" }}>
                      Stock : {product.stock !== undefined && product.stock !== null ? Number(product.stock) : "-"}
                      {product.stock !== undefined && product.stock !== null && Number(product.stock) <= 0 && " (Rupture)"}
                    </div>
                    <div className="d-flex flex-column align-items-end mt-1">
                      <span className="text-primary fw-bold" style={{ fontSize: "1rem" }}>{formatPrice(product.price)}</span>
                      {product.purchasePrice > 0 && (
                        <span className="text-muted fw-bold" style={{ fontSize: "0.7rem" }}>PA: {formatPrice(product.purchasePrice)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {hasMoreProducts && (
              <div 
                className="pos-category-tile shadow-sm mt-3 d-flex align-items-center justify-content-center cursor-pointer w-100"
                style={{ height: "60px", backgroundColor: "var(--pos-bg-panel)" }}
                onClick={() => setVisibleCount(prev => prev + 30)}
              >
                <span className="fw-bold text-white text-uppercase" style={{ fontSize: "0.9rem" }}>
                  {currentProducts.length - visibleCount} AUTRES PRODUITS
                </span>
              </div>
            )}
          </>
        )}
        
        {currentLevel === "leaf" && currentProducts.length === 0 && (
          <div className="text-center text-muted p-5">
            <i className="bi bi-box fs-1 d-block mb-3"></i>
            Aucun produit dans cette catégorie.
          </div>
        )}
      </div>

      <ProductInfoModal 
        show={!!infoProduct} 
        product={infoProduct} 
        onClose={() => setInfoProduct(null)} 
      />
    </div>
  );
};

export default CatalogueBrowser;
